import {useCallback, useEffect, useRef, useState} from 'react';

import PropTypes from 'prop-types';

import {Box, useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui';

/** Outer ring (with border): 57px mobile / 100px desktop. */
const VIDEO_CHROME_RING_SIZE = {base: 57, lg: 100};
/** Inner play glyph (no border): 15×17 mobile / 26×30 desktop. */
const VIDEO_CHROME_PLAY_ICON_WIDTH = {base: 15, lg: 26};
const VIDEO_CHROME_PLAY_ICON_HEIGHT = {base: 17, lg: 30};

/** viewBox 100×100, r=49 + stroke 2 → outer edge fills the rendered ringSize exactly. */
const RING_VIEWBOX = '0 0 100 100';

const PLAY_ICON_VIEWBOX = '0 0 28 36';
const PLAY_ICON_PATH = 'M0 0 L28 18 L0 36 Z';
const PAUSE_ICON_VIEWBOX = '0 0 26 32';
/** Optical centering for play triangle only (pause bars stay centered). */
const PLAY_ICON_MARGIN_LEFT = {base: '3px', lg: '6px'};

/**
 * Blog video chrome: stroke ring at `ringSize`, centered play/pause glyph at `iconWidth` × `iconHeight`.
 */
function VideoChromeGlyph({ringSize, iconWidth, iconHeight, variant = 'play'}) {
  const playIconMarginLeft =
    useBreakpointValue(PLAY_ICON_MARGIN_LEFT) ?? PLAY_ICON_MARGIN_LEFT.base;

  return (
    <Box
      alignItems="center"
      display="flex"
      flexShrink={0}
      height={`${ringSize}px`}
      justifyContent="center"
      position="relative"
      width={`${ringSize}px`}
    >
      <svg
        aria-hidden
        height={ringSize}
        style={{position: 'absolute', inset: 0}}
        viewBox={RING_VIEWBOX}
        width={ringSize}
      >
        <circle cx="50" cy="50" fill="none" r="49" stroke="currentColor" strokeWidth="2" />
      </svg>
      {variant === 'play' ? (
        <svg
          aria-hidden
          height={iconHeight}
          style={{position: 'relative', zIndex: 1, marginLeft: playIconMarginLeft}}
          viewBox={PLAY_ICON_VIEWBOX}
          width={iconWidth}
        >
          <path d={PLAY_ICON_PATH} fill="currentColor" />
        </svg>
      ) : (
        <svg
          aria-hidden
          height={iconHeight}
          style={{position: 'relative', zIndex: 1}}
          viewBox={PAUSE_ICON_VIEWBOX}
          width={iconWidth}
        >
          <rect fill="currentColor" height="32" rx="1" width="9" x="0" y="0" />
          <rect fill="currentColor" height="32" rx="1" width="9" x="17" y="0" />
        </svg>
      )}
    </Box>
  );
}

VideoChromeGlyph.propTypes = {
  iconHeight: PropTypes.number.isRequired,
  iconWidth: PropTypes.number.isRequired,
  ringSize: PropTypes.number.isRequired,
  variant: PropTypes.oneOf(['play', 'pause']),
};

/**
 * Custom chrome over Contentful `<Video />` (Vimeo / YouTube / HTML5 via shared Player API).
 *
 * Renders nothing unless `blogChrome` (blog page / article body layout). Then: play/pause control;
 * hides the glyph during playback until hover or touch (`useBlogArticleVideoChrome`).
 *
 * @param {{ abstractVideo?: object|null, overlayStyles?: object, blogChrome?: boolean, chromeHovered?: boolean, onChromeHoveredChange?: (v: boolean) => void }} props
 */
export const ContentfulVideoPlayOverlay = ({
  abstractVideo,
  overlayStyles,
  blogChrome = false,
  chromeHovered = false,
  onChromeHoveredChange,
}) => {
  const [playing, setPlaying] = useState(false);
  const touchHideTimerRef = useRef(null);

  const overlaySx = overlayStyles?.overlay ?? {};
  const overlayButtonSx = overlayStyles?.overlayButton ?? {};
  const ringSize = useBreakpointValue(VIDEO_CHROME_RING_SIZE) ?? VIDEO_CHROME_RING_SIZE.base;
  const iconWidth =
    useBreakpointValue(VIDEO_CHROME_PLAY_ICON_WIDTH) ?? VIDEO_CHROME_PLAY_ICON_WIDTH.base;
  const iconHeight =
    useBreakpointValue(VIDEO_CHROME_PLAY_ICON_HEIGHT) ?? VIDEO_CHROME_PLAY_ICON_HEIGHT.base;

  useEffect(() => {
    return () => {
      window.clearTimeout(touchHideTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!abstractVideo) return undefined;

    let cancelled = false;
    let intervalId;

    const attach = (player) => {
      let pollPlayingId;

      const sync = () => {
        player
          .isPlaying()
          .then((v) => {
            if (!cancelled) setPlaying(v);
          })
          .catch(() => {});
      };

      sync();
      pollPlayingId = window.setInterval(sync, 450);

      const detach = [];
      if (typeof player.on === 'function') {
        try {
          for (const ev of ['play', 'playing', 'pause', 'ended']) {
            player.on(ev, sync);
            detach.push(() => {
              try {
                player.off?.(ev, sync);
              } catch {
                /* noop */
              }
            });
          }
        } catch {
          /* poll only */
        }
      }

      return () => {
        window.clearInterval(pollPlayingId);
        detach.forEach((fn) => fn());
      };
    };

    let teardownPlayer = () => {};

    const tryAttach = () => {
      let player = abstractVideo.player;

      if (!player && typeof abstractVideo.init === 'function' && !abstractVideo.isInitialized()) {
        abstractVideo.init().catch(() => {});
      }

      player = abstractVideo.player;
      if (!player) return false;

      teardownPlayer();
      teardownPlayer = attach(player);

      return true;
    };

    if (!tryAttach()) {
      intervalId = window.setInterval(() => {
        if (cancelled || tryAttach()) {
          window.clearInterval(intervalId);
        }
      }, 120);
    }

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      teardownPlayer();
    };
  }, [abstractVideo]);

  const toggle = useCallback(async () => {
    if (!abstractVideo) return;

    try {
      if (!abstractVideo.player && typeof abstractVideo.init === 'function' && !abstractVideo.isInitialized()) {
        await abstractVideo.init();
      }

      const player = abstractVideo.player;
      if (!player) return;

      const nowPlaying = await player.isPlaying();

      if (nowPlaying) {
        await player.pause();
      } else {
        try {
          await player.unMute();
        } catch {
          try {
            await player.setMuted(false);
          } catch {
            /* noop */
          }
        }
        await player.play();
      }
    } catch {
      /* noop */
    }
  }, [abstractVideo]);

  const ready = Boolean(abstractVideo);

  /** Blog: hide chrome while autoplay / playback unless hovered or touched to reveal pause. */
  const hideBlogPlayingChrome = blogChrome && playing && ready && !chromeHovered;

  const onOverlayTouchStart = useCallback(() => {
    if (!blogChrome || !playing || !onChromeHoveredChange) return;

    onChromeHoveredChange(true);
    window.clearTimeout(touchHideTimerRef.current);
    touchHideTimerRef.current = window.setTimeout(() => {
      onChromeHoveredChange(false);
    }, 3200);
  }, [blogChrome, playing, onChromeHoveredChange]);

  if (!blogChrome) {
    return null;
  }

  return (
    <Box
      {...overlaySx}
      onMouseEnter={() => {
        if (blogChrome) onChromeHoveredChange?.(true);
      }}
      onMouseLeave={() => {
        if (blogChrome) onChromeHoveredChange?.(false);
      }}
      onTouchStart={blogChrome ? onOverlayTouchStart : undefined}
    >
      <Box
        aria-hidden={hideBlogPlayingChrome}
        aria-label={hideBlogPlayingChrome ? undefined : playing ? 'Pause video' : 'Play video'}
        as="button"
        disabled={!ready}
        tabIndex={hideBlogPlayingChrome ? -1 : 0}
        type="button"
        sx={{
          ...(hideBlogPlayingChrome && {
            opacity: 0,
            visibility: 'hidden',
            pointerEvents: 'none',
          }),
          ...(blogChrome && {
            transitionProperty: 'opacity, visibility',
            transitionDuration: '0.2s',
            transitionTimingFunction: 'ease',
          }),
        }}
        onClick={toggle}
        {...overlayButtonSx}
      >
        {playing ? (
          <VideoChromeGlyph
            iconHeight={iconHeight}
            iconWidth={iconWidth}
            ringSize={ringSize}
            variant="pause"
          />
        ) : (
          <VideoChromeGlyph
            iconHeight={iconHeight}
            iconWidth={iconWidth}
            ringSize={ringSize}
            variant="play"
          />
        )}
      </Box>
    </Box>
  );
};

ContentfulVideoPlayOverlay.propTypes = {
  abstractVideo: PropTypes.object,
  blogChrome: PropTypes.bool,
  chromeHovered: PropTypes.bool,
  onChromeHoveredChange: PropTypes.func,
  overlayStyles: PropTypes.shape({
    overlay: PropTypes.object,
    overlayButton: PropTypes.object,
  }),
};
